import React from "react";
import { FiShield } from "react-icons/fi";

interface LoginOtpFormProps {
  mobileNumber?: string;
  otp: string;
  setOtp: (val: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  loading: boolean;
  error?: string | null;
  timeLeft: number;
  resendCount: number;
  onResend: () => void;
  onBack: () => void;
}

const LoginOtpForm: React.FC<LoginOtpFormProps> = ({
  mobileNumber,
  otp,
  setOtp,
  onSubmit,
  loading,
  error,
  timeLeft,
  resendCount,
  onResend,
  onBack,
}) => {
  return (
    <form onSubmit={onSubmit} className="space-y-6 mt-8 max-w-sm mx-auto w-full">
      {mobileNumber && (
        <div className="text-center mb-4">
          <p className="text-sm text-gray-500">Sent to <span className="font-semibold text-gray-700">{mobileNumber}</span></p>
        </div>
      )}
      <div className="space-y-1.5">
        <label htmlFor="otp" className="text-xs font-semibold text-gray-500 tracking-wide ml-1 uppercase">
          Authorization Code
        </label>
        <div className="relative group transition-all duration-300 focus-within:-translate-y-1">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400 group-focus-within:text-violet-600 transition-colors">
            <FiShield size={18} />
          </div>
          <input
            id="otp"
            type="text"
            value={otp}
            onChange={(e) => {
              // Only allow digits and max 6 chars
              const val = e.target.value.replace(/\D/g, "").slice(0, 6);
              setOtp(val);
            }}
            placeholder="• • • • • •"
            className="block w-full pl-11 pr-4 py-3.5 bg-gray-50 border border-gray-100 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-4 focus:ring-violet-500/10 focus:border-violet-500/50 focus:bg-white transition-all duration-300 text-sm font-medium shadow-sm hover:shadow-md tracking-[0.25em]"
          />
        </div>
      </div>

      {error && (
        <div className="mt-4 bg-red-50/50 border border-red-100 text-red-600 px-4 py-3 rounded-xl shadow-sm text-xs font-medium backdrop-blur-sm animate-pulse-soft">
          <p>{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="w-full flex justify-center items-center py-4 px-4 border border-transparent rounded-2xl shadow-lg shadow-violet-500/20 text-sm font-bold text-white bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 focus:outline-none focus:ring-4 focus:ring-violet-500/20 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all active:scale-[0.98] duration-300 hover:shadow-violet-500/30 mt-2"
      >
        {loading ? (
          <span className="flex items-center gap-2">
            <svg
              className="animate-spin h-5 w-5 text-white/90"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span className="tracking-wide">Validating OTP...</span>
          </span>
        ) : (
          "Secure Login"
        )}
      </button>

      <div className="text-center mt-6 space-y-4">
        <div className="flex flex-col items-center justify-center space-y-1">
          {timeLeft > 0 ? (
            <div className="flex items-center space-x-2 bg-blue-50/50 px-3 py-1.5 rounded-full border border-blue-100">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse"></div>
                <span className="text-xs font-semibold text-blue-600">Resend available in {timeLeft}s</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onResend}
              disabled={resendCount >= 3 || loading}
              className="text-sm font-medium text-violet-600 hover:text-violet-700 hover:underline disabled:text-gray-400 disabled:no-underline transition-colors focus:outline-none"
            >
              {resendCount >= 3 ? "Max resend attempts reached" : "Resend Authorization Code"}
            </button>
          )}
        </div>

        <button
          type="button"
          onClick={onBack}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors focus:outline-none flex items-center justify-center gap-1 mx-auto group"
        >
          <span className="group-hover:-translate-x-0.5 transition-transform">←</span> Not you? Switch Account
        </button>
      </div>
    </form>
  );
};

export default LoginOtpForm;
